import React, { useState, useEffect } from 'react';
import './App.css';
import './MarginSettings.css';
import './MapView.css';
import { fetchBoatInventoryFromSheets } from './services/googleSheets';
import { SettingsProvider } from './context/SettingsContext';
import Header from './components/Header';
import Navigation from './components/Navigation';
import FilterPanel from './components/FilterPanel';
import BoatGrid from './components/BoatGrid';
import MapView from './components/MapViewNew';
import LoadingSpinner from './components/LoadingSpinner';
import BoatModal from './components/BoatModal';
import ContactPage from './components/ContactPage';
import FactoryBoatsPage from './components/FactoryBoatsPage';
import AboutPage from './components/AboutPage';
import AdminDashboard from './components/AdminDashboard';
import RebatesPage from './components/RebatesPage';
import MarginSettings from './components/MarginSettings';
import Login from './components/Login';
import { Map, Grid, Percent } from 'lucide-react';

// Issue Tracking Components  
import IssuesDashboard from './components/issues/IssuesDashboard';
import IssueSubmissionForm from './components/issues/IssueSubmissionForm';
import IssueDetail from './components/issues/IssueDetail';
import AdminIssuesPanel from './components/admin/AdminIssuesPanel';
import AdminLogin from './components/admin/AdminLogin';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Application state
  const [boats, setBoats] = useState([]);
  const [filteredBoats, setFilteredBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    model: '',
    yearMin: '',
    yearMax: '',
    priceMin: '',
    priceMax: '',
    state: '',
    zipCode: '',
    sortBy: 'price-asc'
  });
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [activeSection, setActiveSection] = useState('inventory');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [showMarginSettings, setShowMarginSettings] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    averagePrice: 0,
    statesCount: 0
  });
  
  // Issue tracking state
  const [selectedIssueId, setSelectedIssueId] = useState(null);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem('centurion_auth');
      if (auth) {
        try {
          const authData = JSON.parse(auth);
          // Check if auth is less than 24 hours old
          const isExpired = Date.now() - authData.timestamp > 24 * 60 * 60 * 1000;
          if (!isExpired) {
            setIsAuthenticated(true);
            setCurrentUser(authData.username);
            setIsAdmin(authData.isAdmin || false);
          } else {
            // Clear expired auth
            localStorage.removeItem('centurion_auth');
          }
        } catch (e) {
          localStorage.removeItem('centurion_auth');
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  // Load boats only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadBoats();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    applyFilters();
  }, [boats, filters]);

  const loadBoats = async () => {
    console.log('🌊 App: Starting to load boats from Google Sheets...');
    try {
      setLoading(true);
      setError(null);
      
      // Always use Google Sheets
      const data = await fetchBoatInventoryFromSheets();
      
      console.log('🌊 App: Data received from Google Sheets:', data);
      console.log('🌊 App: Number of boats:', data?.boats?.length || 0);
      
      if (!data || !data.boats) {
        console.warn('⚠️ No boats data in response');
        setError('No boat data received from Google Sheets.');
        setBoats([]);
      } else {
        setBoats(data.boats);
        calculateStats(data.boats);
        console.log('✅ App: Boats successfully loaded from Google Sheets');
        // Log first boat's images for debugging
        if (data.boats[0]) {
          console.log('🖼️ First boat images:', data.boats[0].imageUrls?.length || 0, 'images');
          if (data.boats[0].imageUrls && data.boats[0].imageUrls.length > 0) {
            console.log('Sample URLs:', data.boats[0].imageUrls.slice(0, 2));
          }
        }
      }
    } catch (err) {
      console.error('❌ App: Error loading boats:', err);
      
      let errorMessage = 'Failed to load boat inventory.';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check API credentials.';
        } else if (err.response.status === 404) {
          errorMessage = 'API endpoint not found. Please check the URL.';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Server error: ${err.response.status} - ${err.response.statusText}`;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your internet connection and CORS settings.';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      console.error('🗒️ Error details:', {
        message: err.message,
        response: err.response,
        request: err.request
      });
    } finally {
      setLoading(false);
      console.log('🌊 App: Loading complete');
    }
  };

  const calculateStats = (boats) => {
    const validPrices = boats.filter(b => b.price).map(b => b.price);
    const states = new Set(boats.map(b => b.locationState).filter(Boolean));
    
    setStats({
      total: boats.length,
      averagePrice: validPrices.length > 0 
        ? validPrices.reduce((a, b) => a + b, 0) / validPrices.length 
        : 0,
      statesCount: states.size
    });
  };

  const applyFilters = () => {
    console.log('🔍 Applying filters to', boats.length, 'boats');
    let filtered = [...boats];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(boat =>
        boat.title?.toLowerCase().includes(searchLower) ||
        boat.model?.toLowerCase().includes(searchLower) ||
        boat.description?.toLowerCase().includes(searchLower) ||
        boat.locationCity?.toLowerCase().includes(searchLower)
      );
    }

    // Model filter
    if (filters.model) {
      filtered = filtered.filter(boat =>
        boat.model?.toLowerCase() === filters.model.toLowerCase()
      );
    }

    // Year filter
    if (filters.yearMin) {
      filtered = filtered.filter(boat => boat.year >= parseInt(filters.yearMin));
    }
    if (filters.yearMax) {
      filtered = filtered.filter(boat => boat.year <= parseInt(filters.yearMax));
    }

    // Price filter
    if (filters.priceMin) {
      filtered = filtered.filter(boat => boat.price >= parseFloat(filters.priceMin));
    }
    if (filters.priceMax) {
      filtered = filtered.filter(boat => boat.price <= parseFloat(filters.priceMax));
    }

    // State filter
    if (filters.state) {
      filtered = filtered.filter(boat =>
        boat.locationState?.toLowerCase() === filters.state.toLowerCase()
      );
    }

    // Zip code filter
    if (filters.zipCode) {
      const cleanZip = filters.zipCode.replace(/\r|\n|\s/g, '');
      filtered = filtered.filter(boat => {
        const boatZip = boat.zipCode?.replace(/\r|\n|\s/g, '');
        return boatZip === cleanZip;
      });
    }

    // Sorting
    switch (filters.sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'year-desc':
        filtered.sort((a, b) => (b.year || 0) - (a.year || 0));
        break;
      case 'year-asc':
        filtered.sort((a, b) => (a.year || 0) - (b.year || 0));
        break;
      case 'hours-asc':
        filtered.sort((a, b) => (a.hours || 0) - (b.hours || 0));
        break;
      default:
        break;
    }

    setFilteredBoats(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleLocationFilter = (zipCode) => {
    setFilters(prev => ({
      ...prev,
      zipCode: zipCode
    }));
    setViewMode('grid'); // Switch to grid view to see filtered results
  };

  const handleLogin = (username, adminStatus = false) => {
    setIsAuthenticated(true);
    setCurrentUser(username);
    setIsAdmin(adminStatus);
  };

  const handleLogout = () => {
    localStorage.removeItem('centurion_auth');
    localStorage.removeItem('adminToken'); // Clear admin token on logout
    setIsAuthenticated(false);
    setCurrentUser(null);
    setIsAdmin(false);
    setBoats([]);
    setFilteredBoats([]);
    setActiveSection('inventory');
  };

  // Show loading screen while checking auth
  if (checkingAuth) {
    return (
      <div className="auth-checking">
        <LoadingSpinner />
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Main app content (authenticated users only)
  return (
    <SettingsProvider>
      <div className="App">
        <Header stats={stats} currentUser={currentUser} onLogout={handleLogout} />
        {activeSection !== 'admin' && (
          <Navigation activeSection={activeSection} onSectionChange={setActiveSection} isAdmin={isAdmin} />
        )}
      
      {/* Render different content based on activeSection */}
      {activeSection === 'inventory' ? (
        <div className="main-container">
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            boats={boats}
          />
          
          <div className="content-area">
            {loading ? (
              <LoadingSpinner />
            ) : error ? (
              <div className="error-message">
                <h3>🚨 Error Loading Inventory</h3>
                <p>{error}</p>
                <div className="error-details">
                  <p className="error-hint">Possible issues:</p>
                  <ul>
                    <li>CORS blocking (if running locally)</li>
                    <li>API authentication issues</li>
                    <li>Network connectivity problems</li>
                  </ul>
                  <p className="error-hint">Check the browser console for detailed error information.</p>
                </div>
                <button onClick={loadBoats} className="retry-button">
                  🔄 Retry
                </button>
              </div>
            ) : (
              <>
                <div className="results-header">
                  <h2>Available Inventory ({filteredBoats.length} boats)</h2>
                  <div className="view-controls">
                    <button 
                      className="margin-settings-btn"
                      onClick={() => setShowMarginSettings(true)}
                      title="Margin Settings"
                    >
                      <Percent size={16} />
                    </button>
                    <div className="view-toggle">
                      <button
                        className={viewMode === 'grid' ? 'active' : ''}
                        onClick={() => setViewMode('grid')}
                      >
                        <Grid size={16} />
                        Grid View
                      </button>
                      <button
                        className={viewMode === 'map' ? 'active' : ''}
                        onClick={() => setViewMode('map')}
                      >
                        <Map size={16} />
                        Map View
                      </button>
                    </div>
                  </div>
                </div>
                
                {viewMode === 'grid' ? (
                  <BoatGrid
                    boats={filteredBoats}
                    onBoatClick={setSelectedBoat}
                  />
                ) : (
                  <MapView
                    boats={filteredBoats}
                    onBoatClick={setSelectedBoat}
                    onLocationFilter={handleLocationFilter}
                  />
                )}
              </>
            )}
          </div>
        </div>
      ) : activeSection === 'factory' ? (
        <FactoryBoatsPage />
      ) : activeSection === 'rebates' ? (
        <RebatesPage onNavigate={setActiveSection} />
      ) : activeSection === 'issues' ? (
        <IssuesDashboard 
          onNavigate={setActiveSection} 
          onSelectIssue={setSelectedIssueId}
        />
      ) : activeSection === 'issues-submit' ? (
        <IssueSubmissionForm 
          onNavigate={setActiveSection} 
          onSelectIssue={setSelectedIssueId}
        />
      ) : activeSection === 'issues-detail' ? (
        <IssueDetail 
          issueId={selectedIssueId} 
          onNavigate={setActiveSection}
        />
      ) : activeSection === 'issues-admin' ? (
        <AdminIssuesPanel 
          onNavigate={setActiveSection}
          onSelectIssue={setSelectedIssueId}
        />
      ) : activeSection === 'issues-admin-login' ? (
        <AdminLogin onNavigate={setActiveSection} />
      ) : activeSection === 'contact' ? (
        <ContactPage />
      ) : activeSection === 'about' ? (
        <AboutPage onNavigate={setActiveSection} isAdmin={isAdmin} />
      ) : activeSection === 'admin' && isAdmin ? (
        <AdminDashboard onNavigate={setActiveSection} />
      ) : null}

      {selectedBoat && (
        <BoatModal
          boat={selectedBoat}
          onClose={() => setSelectedBoat(null)}
        />
      )}

      {showMarginSettings && (
        <MarginSettings
          onClose={() => setShowMarginSettings(false)}
        />
      )}
      </div>
    </SettingsProvider>
  );
}

export default App;
